import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Loader2, Users } from 'lucide-react'
import { searchContacts } from '../../hooks/useContacts'

// Chatrone team members — always surfaced first when the field is focused.
// Keeps "Kat" → kat@chatrone.com from getting buried behind 6500 contacts.
const CHATRONE_TEAM = [
  { name: 'Kat Compson',   email: 'kat@chatrone.com',      company: 'Chatrone' },
  { name: 'Ari',           email: 'ari@chatrone.com',       company: 'Chatrone' },
  { name: 'Rodrigo Olaio', email: 'rodrigo@chatrone.com',   company: 'Chatrone' },
  { name: 'Carina',        email: 'carina@chatrone.com',    company: 'Chatrone' },
  { name: 'Aaron Berger',  email: 'aaron@chatrone.com',     company: 'Chatrone' },
]

function teamMatch(q) {
  const lc = q.toLowerCase()
  return CHATRONE_TEAM.filter(t =>
    t.name.toLowerCase().includes(lc) || t.email.toLowerCase().includes(lc)
  ).map(t => ({ contactId: `team:${t.email}`, name: t.name, email: t.email, company: t.company, noEmail: false, isTeam: true }))
}

/**
 * EmailRecipientField — chip-style To/CC/BCC input with contact autocomplete.
 *
 * Props:
 *   label               — "To" | "Cc" | "Bcc"
 *   value (string)      — comma-separated list of emails (controlled)
 *   onChange(str)       — fires with new comma-separated string
 *   autoFocus (bool)
 *   placeholder
 *   suggestedRecipients — [{ name, email }] shown as quick-add chips above input
 */
export default function EmailRecipientField({
  label,
  value = '',
  onChange,
  autoFocus = false,
  placeholder = 'name or email@domain.com',
  suggestedRecipients = [],
  fieldKey = '',
  onChipMoved = null,
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const [focused, setFocused] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const searchGenRef = useRef(0)
  const rootRef = useRef(null)
  // Track the latest query + search fn so connection-recovery can re-run the
  // lookup without a stale closure.
  const queryRef = useRef('')
  const runSearchRef = useRef(null)
  useEffect(() => { queryRef.current = query }, [query])

  // Auto-recover: when the watchdog un-wedges the Supabase client, re-run the
  // current contact search so results populate without the user re-typing.
  // This is what makes the field "always functional without a refresh."
  useEffect(() => {
    const onRecovered = () => {
      const q = queryRef.current
      if (q && q.trim().length >= 2) runSearchRef.current?.(q)
    }
    window.addEventListener('chatrone:connection-recovered', onRecovered)
    return () => window.removeEventListener('chatrone:connection-recovered', onRecovered)
  }, [])

  // Parse current value into chips
  const chips = value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

  const chipSet = new Set(chips.map(c => c.toLowerCase()))

  // Close dropdown on outside click
  useEffect(() => {
    if (!showResults) return
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [showResults])

  const runSearch = (q) => {
    setQuery(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q || q.length < 2) {
      setResults([])
      setShowResults(false)
      setSearching(false)
      return
    }
    // Show matching team members immediately (no debounce)
    const teamHits = teamMatch(q)
    if (teamHits.length > 0) {
      setResults(teamHits)
      setShowResults(true)
      setActiveIdx(0)
    }
    setSearching(true)
    const gen = ++searchGenRef.current
    debounceRef.current = setTimeout(async () => {
      try {
        // 5s timeout guard — Supabase client can hang indefinitely on bad network
        const data = await Promise.race([
          searchContacts(q),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
        ])
        if (gen !== searchGenRef.current) return // stale — a newer search is in flight
        const flat = []
        const seen = new Set(CHATRONE_TEAM.map(t => t.email.toLowerCase()))
        for (const t of teamMatch(q)) {
          if (!chipSet.has(t.email.toLowerCase())) flat.push(t)
        }
        for (const c of data || []) {
          if (seen.has(c.id)) continue
          seen.add(c.id)
          const name = `${c.first_name || ''} ${c.last_name || ''}`.trim()
          const company = c.organization?.name || null
          const emails = Array.isArray(c.emails) ? c.emails : []
          const emailEntries = emails.filter(e => e?.email)
          if (emailEntries.length > 0) {
            for (const e of emailEntries.slice(0, 2)) {
              if (!seen.has(e.email.toLowerCase())) {
                flat.push({ contactId: c.id, name, email: e.email, emailType: e.type || null, company, noEmail: false })
                seen.add(e.email.toLowerCase())
              }
              if (flat.length >= 8) break
            }
          } else {
            flat.push({ contactId: c.id, name, email: null, emailType: null, company, noEmail: true })
          }
          if (flat.length >= 8) break
        }
        setResults(flat)
        setShowResults(flat.length > 0)
        setActiveIdx(0)
      } catch {
        if (gen !== searchGenRef.current) return
        setResults([])
      } finally {
        if (gen === searchGenRef.current) setSearching(false)
      }
    }, 220)
  }
  runSearchRef.current = runSearch

  const addEmail = (addr) => {
    const clean = (addr || '').trim()
    if (!clean) return
    if (chipSet.has(clean.toLowerCase())) return
    // Cancel any pending debounce + invalidate in-flight search so the
    // next typing session starts from a clean state
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null }
    searchGenRef.current++
    setSearching(false)
    const next = [...chips, clean].join(', ')
    onChange?.(next)
    setQuery('')
    setResults([])
    setShowResults(false)
    setTimeout(() => inputRef.current?.focus(), 10)
  }

  const onSelectResult = (r) => {
    if (r.noEmail) return
    addEmail(r.email)
  }

  const removeChip = (i) => {
    const next = chips.filter((_, idx) => idx !== i).join(', ')
    onChange?.(next)
  }

  const commitRaw = () => {
    const clean = query.trim().replace(/[,;]$/, '')
    if (!clean) return
    if (clean.includes('@')) addEmail(clean)
  }

  const onKeyDown = (e) => {
    if (showResults && results.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % results.length); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => (i - 1 + results.length) % results.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        onSelectResult(results[activeIdx])
        return
      }
      if (e.key === 'Escape') { e.preventDefault(); setShowResults(false); return }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      if (query.trim()) {
        e.preventDefault()
        commitRaw()
      }
    }
    if (e.key === 'Backspace' && query === '' && chips.length > 0) {
      e.preventDefault()
      removeChip(chips.length - 1)
    }
  }

  const showTeamSuggestions = false

  // Filter suggestedRecipients to those not already in chips
  const pendingSuggestions = suggestedRecipients.filter(r => r.email && !chipSet.has(r.email.toLowerCase()))

  return (
    <div ref={rootRef} className="relative">
      <label className="input-label">{label}</label>

      {/* Contextual suggested recipients (thread participants, etc.) */}
      {pendingSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mb-1.5">
          <span className="text-[10px] text-warm-400 uppercase tracking-wide mr-0.5">Suggested:</span>
          {pendingSuggestions.map(r => (
            <button
              key={r.email}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addEmail(r.email) }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warm-100 border border-warm-300 text-[11px] text-warm-700 hover:bg-navy-50 hover:border-navy-300 hover:text-navy-700 transition-colors"
            >
              {r.name || r.email}
            </button>
          ))}
        </div>
      )}

      <div
        className={`min-h-[38px] w-full input-field flex flex-wrap items-center gap-1.5 py-1.5 cursor-text transition-colors ${isDragOver ? 'ring-2 ring-navy-400 bg-navy-50/40' : ''}`}
        onClick={() => inputRef.current?.focus()}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('application/chatrone-recipient')) {
            e.preventDefault()
            e.stopPropagation()
            setIsDragOver(true)
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsDragOver(false)
          const raw = e.dataTransfer.getData('application/chatrone-recipient')
          if (!raw) return
          try {
            const { email, fromFieldKey } = JSON.parse(raw)
            if (fromFieldKey === fieldKey) return
            addEmail(email)
            onChipMoved?.(email, fromFieldKey)
          } catch {}
        }}
      >
        {chips.map((chip, i) => (
          <span
            key={`${chip}-${i}`}
            draggable
            onDragStart={(e) => {
              e.stopPropagation()
              e.dataTransfer.setData('application/chatrone-recipient', JSON.stringify({ email: chip, fromFieldKey: fieldKey }))
              e.dataTransfer.effectAllowed = 'move'
            }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-navy-50 border border-navy-200 text-[11px] font-medium text-navy-700 cursor-grab active:cursor-grabbing"
          >
            {chip}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeChip(i) }}
              className="text-navy-400 hover:text-navy-700"
              aria-label="Remove"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => runSearch(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => { setFocused(false); setTimeout(() => { commitRaw(); setShowResults(false) }, 150) }}
          onFocus={() => {
            setFocused(true)
            if (query.length >= 2) setShowResults(results.length > 0)
          }}
          placeholder={chips.length === 0 ? placeholder : ''}
          autoFocus={autoFocus}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm text-warm-900 placeholder-warm-400"
        />
      </div>

      {/* Dropdown: search results OR team member suggestions when empty + focused */}
      {(showResults || showTeamSuggestions) && (
        <div className="absolute top-full mt-1 left-0 right-0 z-30 bg-white border border-warm-300 rounded-lg shadow-lg overflow-hidden max-h-[260px] overflow-y-auto">
          {showTeamSuggestions && (
            <>
              <div className="px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-semibold text-warm-400 uppercase tracking-wide border-b border-warm-100">
                <Users size={10} /> Team
              </div>
              {CHATRONE_TEAM.filter(t => !chipSet.has(t.email.toLowerCase())).map((t, i) => (
                <button
                  key={t.email}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); addEmail(t.email) }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-warm-50`}
                >
                  <div className="w-6 h-6 rounded-full bg-navy-100 border border-navy-200 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-navy-600">
                      {t.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-warm-900 truncate">{t.name}</p>
                    <p className="text-[11px] text-warm-500 truncate">{t.email}</p>
                  </div>
                  <span className="text-[9px] text-navy-400 font-medium uppercase shrink-0">Team</span>
                </button>
              ))}
            </>
          )}
          {showResults && (
            <>
              {searching && (
                <div className="px-3 py-2 text-xs text-warm-400 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Searching…
                </div>
              )}
              {results.map((r, i) => (
                <button
                  key={`${r.contactId}-${r.email || 'noemail'}-${i}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onSelectResult(r) }}
                  onMouseEnter={() => setActiveIdx(i)}
                  disabled={r.noEmail}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    r.noEmail
                      ? 'opacity-50 cursor-default'
                      : i === activeIdx ? 'bg-navy-50' : 'hover:bg-warm-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${r.isTeam ? 'bg-navy-100 border-navy-200' : 'bg-navy-50 border-navy-100'} border`}>
                    <span className="text-[10px] font-bold text-navy-500">
                      {(r.name || r.email || '?').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm truncate ${r.noEmail ? 'text-warm-500' : 'text-warm-900'}`}>
                      {r.name || r.email}
                    </p>
                    <p className="text-[11px] text-warm-500 truncate">
                      {r.noEmail
                        ? `no email on file${r.company ? ` — ${r.company}` : ''}`
                        : `${r.email}${r.company ? ` — ${r.company}` : ''}`
                      }
                    </p>
                  </div>
                  {!r.noEmail && r.isTeam && (
                    <span className="text-[9px] text-navy-400 font-medium uppercase shrink-0">Team</span>
                  )}
                  {!r.noEmail && !r.isTeam && r.emailType && (
                    <span className="text-[9px] font-mono text-warm-400 uppercase shrink-0">{r.emailType}</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
